/**
 * Algoritmo de matching cliente ↔ catálogo (v2 — pH expandido + Q4/Q7/Q8 activos).
 *
 * score(perfume) =
 *     0.38 · match_mouillette(familia)           // reacción olfativa real ciega
 *   + 0.32 · match_preferencias                  // Q1..Q8 declaradas
 *   + 0.20 · match_piel(pH, temp, tipo, text)    // mediciones físicas reales
 *   + 0.10 · match_demografico                   // género, edad, ocasión, estación
 *   + bonus_marca_exclusiva                      // pequeño boost (sólo si score>0.5)
 *
 * Mejoras v2:
 *  - pH dividido en 5 ramas con familias específicas (muy ácido → oud/ámbar, alcalino → florales)
 *  - Q4 dulce-seco, Q7 oriental-fresco, Q8 estilo ACTIVADOS (antes ignorados)
 *  - Cruce temperatura piel + tipo piel para recomendar concentración (EDT vs EDP vs Parfum)
 *  - Notas VERIFICADAS Fragantica preferidas sobre parseadas (cuando están disponibles)
 *  - 5 recomendaciones (1 perfect match + 3 alternativas + 1 descubrimiento)
 *  - Razones detalladas con datos concretos del cliente
 *
 * Mantiene función pura: input → output, sin side effects, testeable sin servidor.
 * Documentación completa: docs/matching.md
 */

import type {
  ClientData,
  MouilletteScores,
  OlfactoryFamily,
  Preferences,
  Product,
  Recommendation,
  SkinMeasures,
} from '@/types';

interface MatchInput {
  client: ClientData;
  skin: SkinMeasures;
  preferences: Preferences;
  mouillettes: MouilletteScores;
  catalog: Product[];
  /** Boost para marcas exclusivas (Aphorismes, Al Jazeera, Reine de Saba) */
  exclusiveBrandBonus?: number;
}

const W_MOUILLETTE = 0.38;
const W_PREFS = 0.32;
const W_SKIN = 0.20;
const W_DEMO = 0.10;
const DEFAULT_EXCLUSIVE_BONUS = 0.08;

// ── Mapeo de mouillettes → familias del catálogo
const FAMILY_TO_MOUILLETTE: Record<OlfactoryFamily, keyof MouilletteScores> = {
  citrico: 'citrico',
  fresco: 'citrico',
  floral: 'floral',
  afrutado: 'floral',
  amaderado: 'amaderado',
  oriental: 'oriental',
  ambar: 'oriental',
  dulce: 'oriental',
  oud: 'oud',
};

/**
 * Helper: devuelve TODAS las notas del producto (priorizando las verificadas Fragantica).
 * Si hay verified disponibles, ÚNICAMENTE se usan esas (más fiables).
 * Si NO hay verified, fallback al parsing local de la descripción Shopify.
 */
function getNotes(p: Product): { top: string[]; heart: string[]; base: string[]; all: string[]; verified: boolean } {
  const hasVerified = !!(
    p.notesTopVerified?.length ||
    p.notesHeartVerified?.length ||
    p.notesBaseVerified?.length
  );
  const top = (hasVerified ? p.notesTopVerified : p.notesTop) || [];
  const heart = (hasVerified ? p.notesHeartVerified : p.notesHeart) || [];
  const base = (hasVerified ? p.notesBaseVerified : p.notesBase) || [];
  const all = [...top, ...heart, ...base].map((n) => n.toLowerCase());
  return { top, heart, base, all, verified: hasVerified };
}

// ─────────────────────────────────────────────────────────────
// FILTROS DUROS — descartan productos que NO respetan las respuestas conscientes
// del cliente. Si el cliente dice "fresco" en Q7, NO le mostramos oud aunque su
// mouillette oud fuese 5/5 (el cliente se sentiría engañado).
// Solo aplican las preguntas donde la respuesta es categórica y excluyente.
// ─────────────────────────────────────────────────────────────
function passesHardFilters(p: Product, prefs: Preferences, client: ClientData): boolean {
  if (!p.family) return true; // sin familia identificada → no podemos filtrar, dejamos pasar

  // Q7 oriental-fresco — más estricto
  if (prefs.q7_oriental_fresco === 'fresco') {
    // Si pide fresco, NUNCA oud/oriental/ambar pesado
    if (['oud', 'oriental', 'ambar'].includes(p.family)) return false;
  } else if (prefs.q7_oriental_fresco === 'oriental') {
    // Si pide oriental, NUNCA cítrico/fresco puro
    if (['citrico', 'fresco'].includes(p.family)) return false;
  }

  // Q1 ambiente — descarta familias incompatibles con la imagen mental
  if (prefs.q1_ambiente === 'jardin_andalusi') {
    // Quien se imagina jardín andalusí NO espera oud denso
    if (p.family === 'oud') return false;
  } else if (prefs.q1_ambiente === 'bosque_lluvia') {
    // Bosque/lluvia NO va con oriental dulce ni oud
    if (['oud', 'dulce'].includes(p.family)) return false;
  }

  // Q4 dulce-seco — descarta extremos contrarios
  // (matching de notas se hace además en scorePreferences)
  // No descartamos por familia aquí porque dulce/seco vive en las notas

  // Género: cross-gender se descarta si el cliente declara género
  if (p.gender && p.gender !== 'unisex' && client.gender && client.gender !== 'no_decir') {
    if (p.gender !== client.gender) return false;
  }

  return true;
}

export function recommend(input: MatchInput, topK = 5): Recommendation[] {
  const { client, skin, preferences, mouillettes, catalog } = input;
  const exclusiveBonus = input.exclusiveBrandBonus ?? DEFAULT_EXCLUSIVE_BONUS;

  const scored: Recommendation[] = catalog
    .filter((p) => p.inStock)
    .filter((p) => passesHardFilters(p, preferences, client))
    .map((p) => {
      const reasons: string[] = [];

      const mScore = scoreMouillette(p, mouillettes, reasons);
      const pScore = scorePreferences(p, preferences, reasons);
      const sScore = scoreSkin(p, skin, reasons);
      const dScore = scoreDemographic(p, client, reasons);

      let total =
        W_MOUILLETTE * mScore +
        W_PREFS * pScore +
        W_SKIN * sScore +
        W_DEMO * dScore;

      if (p.isExclusive && total > 0.5) {
        total += exclusiveBonus;
        reasons.push('Marca exclusiva Elixir');
      }
      total = Math.min(1, total);

      return {
        productId: p.id,
        product: p,
        score: total,
        reasons,
        matchPct: Math.round(total * 100),
      };
    })
    .sort((a, b) => b.score - a.score);

  return diversify(scored, topK);
}

// ─────────────────────────────────────────────────────────────
// Sub-scorers (cada uno devuelve 0..1)
// ─────────────────────────────────────────────────────────────

function scoreMouillette(p: Product, m: MouilletteScores, reasons: string[]): number {
  if (!p.family) return 0.5;
  const key = FAMILY_TO_MOUILLETTE[p.family];
  const raw = m[key] ?? 3; // 1..5
  const norm = (raw - 1) / 4; // 0..1
  if (norm >= 0.75) reasons.push(`Tu reacción ciega a la mouillette ${key} fue alta (${raw}/5)`);
  else if (norm >= 0.5) reasons.push(`Tu mouillette ${key} te resultó agradable`);
  return norm;
}

function scorePreferences(p: Product, prefs: Preferences, reasons: string[]): number {
  let score = 0;
  let weight = 0;
  const { all: allNotes } = getNotes(p);
  const allNotesStr = allNotes.join(' ');

  // ── Q1 ambiente → familia
  const ambienteMap: Record<Preferences['q1_ambiente'], OlfactoryFamily[]> = {
    desierto_atardecer: ['oriental', 'ambar', 'oud'],
    jardin_andalusi: ['floral', 'afrutado'],
    mercado_especias: ['oriental', 'dulce', 'ambar'],
    bosque_lluvia: ['amaderado', 'fresco'],
  };
  if (p.family && ambienteMap[prefs.q1_ambiente].includes(p.family)) {
    score += 1;
    reasons.push('Coincide con el ambiente que te atrae');
  }
  weight += 1;

  // ── Q2 palabra → estilo
  const palabraMap: Record<Preferences['q2_palabra'], OlfactoryFamily[]> = {
    elegante: ['floral', 'amaderado'],
    sensual: ['oriental', 'oud', 'ambar'],
    fresco: ['citrico', 'fresco'],
    misterioso: ['oud', 'ambar', 'oriental'],
  };
  if (p.family && palabraMap[prefs.q2_palabra].includes(p.family)) {
    score += 1;
    reasons.push(`Carácter "${prefs.q2_palabra}" alineado con su familia`);
  }
  weight += 1;

  // ── Q3 estela → projection
  if (p.projection) {
    const diff = Math.abs(p.projection - prefs.q3_estela);
    score += 1 - diff / 4;
    weight += 1;
  }

  // ── Q4 dulce-seco (NUEVO — antes ignorado)
  // dulce ↔ notas dulces (vainilla, dátil, caramelo, miel, praline, oud azucarado)
  // seco ↔ notas secas (oud, cuero, tabaco, vetiver, abedul)
  // medio ↔ acepta todo
  const DULCE_TOKENS = ['vainilla', 'vanilla', 'dátil', 'datil', 'date', 'caramelo', 'caramel', 'miel', 'honey', 'praline', 'azúcar', 'azucar', 'tonka', 'gourmand', 'dulce', 'sweet'];
  const SECO_TOKENS = ['oud', 'agarwood', 'cuero', 'leather', 'tabaco', 'tobacco', 'vetiver', 'abedul', 'birch', 'cedro seco', 'humo', 'smoke'];
  const dulceMatches = DULCE_TOKENS.filter((t) => allNotesStr.includes(t)).length;
  const secoMatches = SECO_TOKENS.filter((t) => allNotesStr.includes(t)).length;
  if (prefs.q4_dulce_seco === 'dulce') {
    if (dulceMatches >= 2) { score += 1; reasons.push('Perfil dulce que buscas (vainilla/caramelo/dátiles)'); }
    else if (dulceMatches === 1) score += 0.7;
    else if (secoMatches >= 2) score += 0.1;
    else score += 0.4;
    weight += 1;
  } else if (prefs.q4_dulce_seco === 'seco') {
    if (secoMatches >= 2) { score += 1; reasons.push('Perfil seco que buscas (oud/cuero/madera)'); }
    else if (secoMatches === 1) score += 0.7;
    else if (dulceMatches >= 2) score += 0.1;
    else score += 0.4;
    weight += 1;
  } else if (prefs.q4_dulce_seco === 'medio') {
    score += 0.75; // perfil intermedio funciona con la mayoría
    weight += 1;
  }

  // ── Q5 notas familiares (multi-select) → buscar matches en TODAS las notas
  const notaMap: Record<string, string[]> = {
    vainilla: ['vainilla', 'vanilla'],
    citrico: ['bergamota', 'limón', 'limon', 'naranja', 'pomelo', 'mandarina', 'lemon', 'citron', 'lime'],
    madera: ['sándalo', 'sandalo', 'sandalwood', 'cedro', 'cedar', 'vetiver', 'pachuli', 'patchouli', 'guaiac'],
    floral: ['rosa', 'rose', 'jazmín', 'jazmin', 'jasmine', 'azahar', 'iris', 'tuberosa', 'tuberose', 'ylang'],
    oud: ['oud', 'agarwood'],
  };
  const notasFav = prefs.q5_notas_familiares ?? [];
  if (notasFav.length > 0) {
    const matched = notasFav.filter((nf) =>
      (notaMap[nf] ?? []).some((n) => allNotesStr.includes(n))
    );
    if (matched.length > 0) {
      score += matched.length / notasFav.length;
      reasons.push(
        matched.length === 1
          ? `Contiene la nota ${matched[0]} que reconoces`
          : `Contiene notas ${matched.join(', ')} que reconoces`
      );
    }
    weight += 1;
  }

  // ── Q6 intensidad habitual
  if (p.intensity) {
    const target = prefs.q6_intensidad_habitual; // 1..4
    const diff = Math.abs(p.intensity - target);
    score += 1 - diff / 4;
    weight += 1;
  }

  // ── Q7 oriental-fresco (NUEVO — antes ignorado)
  // oriental ↔ familias oriental/oud/ambar/amaderado
  // fresco ↔ familias citrico/fresco/floral
  // medio ↔ todo
  if (p.family && prefs.q7_oriental_fresco) {
    const orientalFams: OlfactoryFamily[] = ['oriental', 'oud', 'ambar', 'amaderado'];
    const frescoFams: OlfactoryFamily[] = ['citrico', 'fresco', 'floral'];
    if (prefs.q7_oriental_fresco === 'oriental') {
      if (orientalFams.includes(p.family)) { score += 1; reasons.push('Carácter oriental marcado'); }
      else if (frescoFams.includes(p.family)) score += 0.2;
      else score += 0.5;
      weight += 1;
    } else if (prefs.q7_oriental_fresco === 'fresco') {
      if (frescoFams.includes(p.family)) { score += 1; reasons.push('Perfil fresco occidental que prefieres'); }
      else if (orientalFams.includes(p.family)) score += 0.2;
      else score += 0.5;
      weight += 1;
    } else if (prefs.q7_oriental_fresco === 'medio') {
      score += 0.75;
      weight += 1;
    }
  }

  // ── Q8 estilo (NUEVO — antes ignorado)
  // al_pasar ↔ proyección 1-2 (sutil, sólo cerca te lo huelen)
  // intimo ↔ proyección 1-2 + longevidad alta (parfum/extrait)
  // medio ↔ proyección 3 (balance)
  if (prefs.q8_estilo && p.projection) {
    if (prefs.q8_estilo === 'al_pasar') {
      // El usuario quiere algo sutil — proyección baja (1-2)
      if (p.projection <= 2) { score += 1; reasons.push('Estela sutil "al pasar"'); }
      else if (p.projection === 3) score += 0.5;
      else score += 0.2;
      weight += 1;
    } else if (prefs.q8_estilo === 'intimo') {
      // Íntimo = baja proyección pero alta longevidad
      const isParfum = p.concentration === 'Parfum' || p.concentration === 'Extrait';
      if (p.projection <= 2 && isParfum) { score += 1; reasons.push('Aura íntima de larga duración'); }
      else if (p.projection <= 2) score += 0.75;
      else score += 0.4;
      weight += 1;
    } else if (prefs.q8_estilo === 'medio') {
      if (p.projection === 3) { score += 1; reasons.push('Proyección equilibrada'); }
      else if (p.projection >= 2 && p.projection <= 4) score += 0.75;
      else score += 0.4;
      weight += 1;
    }
  }

  return weight > 0 ? score / weight : 0.5;
}

/**
 * Lógica pH expandida a 5 ramas con familias específicas por rango.
 * Combinada con temperatura piel + tipo piel para recomendar concentración correcta.
 */
function scoreSkin(p: Product, skin: SkinMeasures, reasons: string[]): number {
  let score = 0;
  let weight = 0;

  // ── pH expandido: 5 ramas con familias específicas ──
  if (p.family) {
    const ph = skin.ph;
    // Mapeo por rango de pH:
    //  pH ≤ 5.0 (muy ácido) → oud, ámbar, resinas amplifican y duran +30-40%
    //  pH 5.0-5.5 (ácido) → orientales, especiados rinden bien
    //  pH 5.5-6.0 (neutro) → casi todo funciona (ideal)
    //  pH 6.0-6.5 (lig. alcalino) → florales, frutales y cítricos brillan
    //  pH > 6.5 (alcalino) → frescos delicados; oud y orientales se evaporan rápido
    let phMatch = false;
    if (ph <= 5.0) {
      const favored: OlfactoryFamily[] = ['oud', 'ambar', 'oriental'];
      if (favored.includes(p.family)) {
        score += 1; phMatch = true;
        reasons.push('Tu pH muy ácido potencia oud y resinas');
      } else if (['amaderado', 'dulce'].includes(p.family)) {
        score += 0.7;
      } else {
        score += 0.3;
      }
    } else if (ph <= 5.5) {
      const favored: OlfactoryFamily[] = ['oriental', 'ambar', 'oud', 'dulce'];
      if (favored.includes(p.family)) {
        score += 1; phMatch = true;
        reasons.push('Tu pH ácido realza notas orientales y especiadas');
      } else if (['amaderado', 'afrutado'].includes(p.family)) {
        score += 0.65;
      } else {
        score += 0.4;
      }
    } else if (ph <= 6.0) {
      // pH neutro — todo funciona razonablemente
      score += 0.8; phMatch = true;
      reasons.push('Tu pH neutro funciona bien con cualquier familia');
    } else if (ph <= 6.5) {
      const favored: OlfactoryFamily[] = ['floral', 'afrutado', 'citrico', 'fresco'];
      if (favored.includes(p.family)) {
        score += 1; phMatch = true;
        reasons.push('Tu pH ligeramente alcalino realza florales y cítricos');
      } else if (['amaderado'].includes(p.family)) {
        score += 0.6;
      } else {
        score += 0.4;
      }
    } else {
      // pH alcalino (>6.5)
      const favored: OlfactoryFamily[] = ['floral', 'fresco', 'citrico'];
      const disfavored: OlfactoryFamily[] = ['oud', 'oriental'];
      if (favored.includes(p.family)) {
        score += 1; phMatch = true;
        reasons.push('Tu pH alcalino brilla con frescos y florales delicados');
      } else if (disfavored.includes(p.family)) {
        score += 0.2; // se va rápido en piel alcalina
      } else {
        score += 0.5;
      }
    }
    weight += 1.5; // pH pesa más que otros sub-factores piel
    void phMatch;
  }

  // ── Cruce temperatura + tipo piel → concentración recomendada ──
  // grasa + caliente → EDT/EDP (más concentración satura)
  // grasa + fría → EDP/Parfum
  // seca + caliente → EDP (la piel evapora rápido, hace falta más)
  // seca + fría → Parfum/Extrait (máxima fijación)
  // normal templada → cualquiera
  if (p.concentration) {
    const skinIsHot = skin.tempC >= 34;
    const skinIsCold = skin.tempC <= 32;
    let target: string[] = [];
    if (skin.type === 'grasa' && skinIsHot) target = ['EDT'];
    else if (skin.type === 'grasa' && skinIsCold) target = ['EDP', 'Parfum'];
    else if (skin.type === 'grasa') target = ['EDP', 'EDT'];
    else if (skin.type === 'seca' && skinIsCold) target = ['Parfum', 'Extrait'];
    else if (skin.type === 'seca') target = ['EDP', 'Parfum'];
    else target = ['EDP', 'EDT']; // normal

    if (target.includes(p.concentration)) {
      score += 1;
      if (skin.type === 'seca' && (p.concentration === 'Parfum' || p.concentration === 'Extrait')) {
        reasons.push('Concentración alta ideal para tu piel seca');
      } else if (skin.type === 'grasa' && p.concentration === 'EDT') {
        reasons.push('EDT ligero, perfecto para tu piel grasa');
      } else {
        reasons.push(`${p.concentration} adecuado para tu tipo de piel`);
      }
    } else {
      score += 0.5;
    }
    weight += 1;
  }

  // ── Textura piel ── (poco peso pero suma)
  if (p.family) {
    if (skin.texture === 'rugosa' && (['oud', 'ambar', 'oriental'] as OlfactoryFamily[]).includes(p.family)) {
      score += 1;
    } else if (skin.texture === 'lisa' && (['fresco', 'citrico', 'floral', 'amaderado'] as OlfactoryFamily[]).includes(p.family)) {
      score += 1;
    } else {
      score += 0.6;
    }
    weight += 0.7;
  }

  return weight > 0 ? score / weight : 0.5;
}

function scoreDemographic(p: Product, client: ClientData, reasons: string[]): number {
  let score = 0;
  let weight = 0;

  // Género
  if (p.gender) {
    if (p.gender === 'unisex') {
      score += 1;
    } else if (client.gender === 'no_decir') {
      score += 0.85;
    } else if (p.gender === client.gender) {
      score += 1;
    } else {
      score += 0.15;
    }
    weight += 1;
  }

  const tags = p.tags.map((t) => t.toLowerCase());

  // Ocasión (multi-select)
  const occasionTagMap: Record<string, string[]> = {
    diario: ['diario', 'daily', 'office'],
    trabajo: ['oficina', 'office', 'diario'],
    noche: ['noche', 'night', 'evening'],
    especial: ['especial', 'occasion', 'lujo', 'luxury'],
  };
  const occasions = client.occasions ?? [];
  if (occasions.length > 0) {
    const matched = occasions.filter((occ) =>
      (occasionTagMap[occ] ?? []).some((t) => tags.includes(t))
    );
    if (matched.length > 0) {
      score += matched.length / occasions.length;
      reasons.push(
        matched.length === occasions.length
          ? `Versátil para todas tus ocasiones`
          : `Recomendado para ${matched.join(', ')}`
      );
    }
    weight += 1;
  }

  // Estación + cruce con familia (más fino que antes)
  const seasons = client.seasons ?? [];
  if (seasons.length > 0) {
    const seasonTagMap: Record<string, string[]> = {
      primavera: ['primavera', 'spring', 'fresco', 'floral'],
      verano:    ['verano', 'summer', 'fresco', 'cítrico', 'citrico'],
      otono:     ['otoño', 'otono', 'autumn', 'fall', 'amaderado'],
      invierno:  ['invierno', 'winter', 'caliente', 'oriental', 'oud'],
    };
    const seasonFamilyMap: Record<string, OlfactoryFamily[]> = {
      primavera: ['floral', 'fresco', 'citrico'],
      verano:    ['fresco', 'citrico', 'floral'],
      otono:     ['amaderado', 'ambar', 'dulce'],
      invierno:  ['oriental', 'oud', 'ambar', 'amaderado'],
    };
    const matched = seasons.filter((s) => {
      if ((seasonTagMap[s] ?? []).some((t) => tags.includes(t))) return true;
      if (p.family && (seasonFamilyMap[s] ?? []).includes(p.family)) return true;
      return false;
    });
    if (matched.length > 0) {
      score += matched.length / seasons.length;
      if (matched.length === seasons.length && seasons.length > 1) {
        reasons.push('Apto para todas tus estaciones');
      } else if (matched.length === 1) {
        reasons.push(`Apropiado para ${matched[0]}`);
      } else {
        reasons.push(`Apropiado para ${matched.join(', ')}`);
      }
    }
    weight += 1;
  }

  return weight > 0 ? score / weight : 0.5;
}

// ─────────────────────────────────────────────────────────────
// Diversidad del top-K con TIERS (perfect + alternativas + descubrimiento).
//
// Para topK=5:
//   - Pos 1: el mejor score absoluto (perfect match)
//   - Pos 2-4: alternativas con familias distintas + max 1 exclusiva
//   - Pos 5: "descubrimiento" — un perfume con score moderado pero familia
//     diferente al top 1 (para que el cliente vea variedad)
// ─────────────────────────────────────────────────────────────
function diversify(scored: Recommendation[], topK: number): Recommendation[] {
  if (scored.length === 0) return [];
  const out: Recommendation[] = [];
  const familiesUsed = new Set<string>();
  let exclusivesUsed = 0;
  const MAX_EXCLUSIVES = 1; // máximo 1 perfume de marca exclusiva (Al Jazeera/Aphorismes/Reine de Saba)
  // Marcas normales (Lattafa, Armaf, Afnan, etc.): SIN límite — si matchean alto, todos dentro.

  function addToOut(r: Recommendation) {
    out.push(r);
    if (r.product.family) familiesUsed.add(r.product.family);
    if (r.product.isExclusive) exclusivesUsed++;
  }

  function canAdd(r: Recommendation, opts: { allowSameFamily?: boolean } = {}): boolean {
    if (out.find((x) => x.productId === r.productId)) return false;
    const fam = r.product.family ?? '__no_fam__';
    if (!opts.allowSameFamily && familiesUsed.has(fam)) return false;
    if (r.product.isExclusive && exclusivesUsed >= MAX_EXCLUSIVES) return false;
    return true;
  }

  // Pos 1: el mejor score absoluto (sin restricciones)
  addToOut(scored[0]);

  // Pos 2..(topK-1): diversidad de familias + límite marca
  for (const r of scored.slice(1)) {
    if (out.length >= topK - 1) break;
    if (canAdd(r)) addToOut(r);
  }

  // Pos topK (descubrimiento): familia distinta al top1, score decente
  if (out.length < topK) {
    const top1Family = scored[0].product.family;
    for (const r of scored) {
      if (out.length >= topK) break;
      if (r.score < 0.4) break;
      if (top1Family && r.product.family === top1Family) continue;
      if (canAdd(r)) { addToOut(r); break; }
    }
  }

  // Pasada final: relajar diversidad de familia, mantener límite marca/exclusivas
  if (out.length < topK) {
    for (const r of scored) {
      if (out.length >= topK) break;
      if (canAdd(r, { allowSameFamily: true })) addToOut(r);
    }
  }

  return out;
}
